import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { departmentsService, type DepartmentQueryParams } from '../../services/departments.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { Dropdown } from 'sharedUi/Dropdown';
import { useDebounce } from '../../hooks/useDebounce';
import type { Department } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { ProjectCell } from '../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../components/common/saas-data-table';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import { formatDate } from 'sharedUi/dateUtils';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [
  { field: 'name', header: 'Department Name', filter: true },
  { field: 'departmentCd', header: 'Department Code', filter: true },
];

const DepartmentsList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const { navigateToView, navigateToEdit, navigateToCreate } = useSecureNavigation({
    entity: 'departments',
    basePath: '/departments',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.DEPARTMENTS) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.DEPARTMENTS);
  const canDelete = useCanDelete(JOB_NAMES.DEPARTMENTS);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  const [showCore, setShowCore] = useState(false);
  // >1 accessible project ≈ platform/partner admin who may filter + re-scope.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);

  // Pagination states for server-side pagination
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Sorting states
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  // Debounce search text (500ms delay)
  const debouncedSearchText = useDebounce(searchText, 500);

  const showError = useCallback((message: string) => {
    toast.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: 10000,
    });
  }, []);

  const fetchDepartments = useCallback(async (
    page: number = 1,
    size: number = pageSize,
    sortBy: string | null = sortField,
    order: 1 | -1 | null = sortOrder,
    activeFilters: Record<string, any> = filters
  ) => {
    try {
      setLoading(true);
      const params: DepartmentQueryParams = {
        page,
        page_size: size,
        include_deleted: includeDeleted,
      };

      if (debouncedSearchText && debouncedSearchText.trim()) {
        params.search = debouncedSearchText.trim();
      }

      // Add sorting parameters
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = order === 1 ? 'asc' : 'desc';
      }

      const filtersParam = buildColumnFiltersParam(activeFilters);
      if (filtersParam) params.filters = filtersParam;

      if (selectedProjectId) params.app_id = selectedProjectId;
      params.include_global = showCore;

      const response = await departmentsService.getAll(params);
      const mappedData = (response.data || []).map((dept: any) => ({ ...dept, id: dept._id }));
      setDepartments(mappedData);

      // Handle both totalItems and total from API response
      const total = response.pagination?.totalItems ?? (response.pagination as any)?.total ?? 0;
      setTotalRecords(total);
    } catch (err: any) {
      console.error('Failed to fetch departments:', err);
      showError(extractErrorMessage(err, 'Failed to load departments. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showCore, pageSize, sortField, sortOrder, filters, showError]);

  // Handle page change from DataTable
  const handlePageChange = (event: any) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    setFirst(event.first);
    setPageSize(event.rows);
    fetchDepartments(newPage, event.rows, sortField, sortOrder);
  };

  // Handle sort event - server-side sorting
  const handleSort = (event: any) => {
    const newSortField = event.sortField;
    const newSortOrder = event.sortOrder as 1 | -1 | null;

    setSortField(newSortField);
    setSortOrder(newSortOrder);

    // Reset to first page when sorting changes
    setFirst(0);
    fetchDepartments(1, pageSize, newSortField, newSortOrder);
  };

  const handleFilter = (event: any) => {
    const next = event.filters || {};
    setFilters(next);
    setFirst(0);
    fetchDepartments(1, pageSize, sortField, sortOrder, next);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setFirst(0);
    fetchDepartments(1, pageSize);
  }, [includeDeleted, debouncedSearchText, selectedProjectId, showCore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = (id: string | number) => {
    navigateToView(String(id));
  };

  const handleEdit = (id: string | number) => {
    navigateToEdit(String(id));
  };

  const handleDeleteClick = (id: string | number) => {
    setSelectedId(id as string);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedId) return;

    try {
      setDeleteLoading(true);
      await departmentsService.delete(selectedId);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      fetchDepartments(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);
    } catch (err: any) {
      console.error('Failed to delete department:', err);
      setDeleteDialogOpen(false);
      setSelectedId(null);
      showError(extractErrorMessage(err, 'Failed to delete department'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedId(null);
  };

  const columns: SaasColumn<Department>[] = useMemo(() => {
    const cols: SaasColumn<Department>[] = [
      {
        field: 'name',
        header: 'Department Name',
        sortable: true,
        filter: true,
        hideable: false,
      },
      {
        field: 'departmentCd',
        header: 'Department Code',
        sortable: true,
        filter: true,
        body: (rowData: Department) => rowData.departmentCd || '-',
      },
      {
        field: 'createdAt',
        header: 'Created At',
        sortable: true,
        body: (rowData: Department) => formatDate(rowData.createdAt),
      },
    ];

    // Only show Status column when includeDeleted is active
    if (includeDeleted) {
      cols.push({
        field: 'status',
        header: 'Status',
        body: (rowData: Department) => {
          const deleted = rowData.isDelete;
          return (
            <span
              className={`saas-status-dot ${deleted ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}
            >
              {deleted ? 'Deleted' : 'Active'}
            </span>
          );
        },
      });
    }

    cols.push({
      field: 'appId',
      header: 'Project',
      body: (row: any) => <ProjectCell appId={row.appId} projects={accessibleProjects} />,
    });

    // Always add actions column at the end
    cols.push({
      field: 'actions',
      header: 'Actions',
      align: 'center',
      hideable: false,
      body: (rowData: Department) => {
        const isDeleted = rowData.isDelete;

        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View"
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors action-view-btn"
              onClick={() => handleView(rowData._id)}
              data-testid="DepartmentsList.Action.View"
              data-pr-tooltip="View"
              data-pr-position="top"
            >
              <i className="pi pi-eye text-sm text-green-600 dark:text-green-400" style={{ fontSize: '1.125rem' }} />
            </button>

            {!isDeleted && (
              <>
                {canUpdate && (
                  <button aria-label="Edit"
                    className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors action-edit-btn"
                    onClick={() => handleEdit(rowData._id)}
                    data-testid="DepartmentsList.Action.Edit"
                    data-pr-tooltip="Edit"
                    data-pr-position="top"
                  >
                    <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.125rem' }} />
                  </button>
                )}

                {canDelete && (
                  <button aria-label="Delete"
                    className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors action-delete-btn"
                    onClick={() => handleDeleteClick(rowData._id)}
                    data-testid="DepartmentsList.Action.Delete"
                    data-pr-tooltip="Delete"
                    data-pr-position="top"
                  >
                    <i className="pi pi-trash text-red-600" style={{ fontSize: '1.125rem' }} />
                  </button>
                )}
              </>
            )}
          </div>
        );
      },
    });

    return cols;
  }, [canUpdate, canDelete, canManageProjects, includeDeleted, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.DEPARTMENTS}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-Departments-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-building"
          title="Departments"
          subtitle="Manage departments and their codes"
          testId="Departments.Header"
        />

        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="departments"
            columns={appId ? columns.filter((c) => c.field !== 'appId') : columns}
            data={departments}
            loading={loading}
            dataKey="_id"
            emptyMessage="No departments found"
            first={first}
            rows={pageSize}
            totalRecords={totalRecords}
            onPage={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={handleSort}
            filters={filters}
            onFilter={handleFilter}
            globalSearch={searchText}
            onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search departments..."
            testId="DepartmentsList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                      value: p._id,
                    }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                    data-testid="DepartmentsList.Filter.Project"
                  />
                )}
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="Departments.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">
                  {totalRecords} {totalRecords === 1 ? 'record' : 'records'}
                </span>
                <RefreshButton
                  onClick={() => fetchDepartments(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder)}
                  loading={loading}
                />
                {canCreate && (
                  <Button
                    label="Create Department"
                    icon="pi pi-plus"
                    onClick={() => navigateToCreate()}
                    testId="DepartmentsList.Button.Create"
                    size="small"
                  />
                )}
              </>
            }
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          visible={deleteDialogOpen}
          onCancel={handleDeleteCancel}
          onDelete={handleDeleteConfirm}
          message="Are you sure you want to delete this department? This will mark it as deleted."
          testId="DepartmentsList.Dialog.Delete"
          loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default DepartmentsList;
